import PageLoadedQueue from "../PageLoadedQueue";
import {getCookie, setCookie} from "../Plugins/Cookies";
import {RenderModal} from "../Functions/RenderModal";
import throttle from "../Plugins/Throttle";

class ContentPopup {

    protected popupIdentifier: string;
    protected cookieMode: string;
    protected displayMode: string;
    protected displayDelay: number;
    protected displayVhScrollThreshold: number;
    protected displayCartTargetProductItemIds: array;


    constructor(popupData: array) {
        this.popupIdentifier = popupData.identifier;
        this.cookieMode = popupData.cookieMode;
        this.displayMode = popupData.displayMode;
        this.displayDelay = popupData.displayDelay;
        this.displayVhScrollThreshold = popupData.displayVhScrollThreshold;
        this.displayCartTargetProductItemIds = popupData.displayCartTargetProductItemIds;

        this.cookieName = this.cookieMode === 'persistent' ? 'popup-view-log' : 'popup-view-log-session';
        this.handleCookiebotEvent = this.handleCookiebotEvent.bind(this);

        this.handleScroll = this.handleScroll.bind(this);
        this.scrollListener = throttle(this.handleScroll, 100);

        if (typeof window.Cookiebot !== 'undefined') {
            if (window.Cookiebot.consented || window.Cookiebot.declined) {
                // made choice already
                this.activateDisplayMode();
            } else {
                window.addEventListener('CookiebotOnAccept', this.handleCookiebotEvent);
                window.addEventListener('CookiebotOnDecline', this.handleCookiebotEvent);
            }
        } else {
            this.activateDisplayMode();
        }
    }

    handleCookiebotEvent(e) {
        if (window.Cookiebot.consent.necessary) {
            window.removeEventListener('CookiebotOnAccept', this.handleCookiebotEvent);
            window.removeEventListener('CookiebotOnDecline', this.handleCookiebotEvent);
            this.activateDisplayMode(true);
        }
    }

    activateDisplayMode(comingFromCookieConsent: bool = false) {
        if (this.displayMode === 'requested') {
            this.showPopup();
        } else if (this.displayMode === 'delay') {
            this.showPopupWithDelay(this.displayDelay * 1000 + (comingFromCookieConsent ? 0 : 1000));
        } else if (this.displayMode === 'scroll-threshold') {
            window.addEventListener('scroll', this.scrollListener);
            this.handleScroll(); //already check if past scroll threshold
        } else if (this.displayMode === 'product-added-to-cart' || this.displayMode === 'product-requested') {
            document.addEventListener('plandent.cart.added', (e) => {
                if (this.displayCartTargetProductItemIds.length === 0 || (this.displayCartTargetProductItemIds && this.displayCartTargetProductItemIds.includes('' + e.detail.itemId))) {
                    this.showPopupWithDelay(1250);
                }
            })
            document.addEventListener('plandent.cart.addedWithRedirect', (e) => {
                if (this.displayCartTargetProductItemIds.length === 0 || (this.displayCartTargetProductItemIds && this.displayCartTargetProductItemIds.includes('' + e.detail.itemId))) {
                    e.preventDefault();
                    this.showPopupWithDelay(500, () => {
                        window.location.href = e.detail.redirect;
                    });
                }
            });
        }
    }

    handleScroll(e: Event) {
        if (
            (window.scrollY > window.innerHeight * (this.displayVhScrollThreshold / 100)) // scroll progress
            || (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight) // reached end of document, always trigger here
        ) {
            this.showPopup();
            window.removeEventListener('scroll', this.scrollListener);
        }
    }

    showPopupWithDelay(delay: Number = 0, callback: Function = null) {
        if (delay > 0) {
            setTimeout(() => {
                this.showPopup(callback);
            }, delay);
        } else {
            this.showPopup(callback);
        }
    }

    async showPopup(callback: Function = null) {

        let htmlContent = null;

        if (this.displayMode !== 'requested') {
            htmlContent = await this.loadPopupHtml();
            if (!htmlContent) {
                console.log('ContentPopup: No content loaded for popup with identifier: ' + this.popupIdentifier);
                return;
            }
        }

        const popupModal = RenderModal.init(
            '',
            '<div class="modal-content-loading"></div>',
            null,
            false,
            null,
            null,
            (e) => {
                // set cookie after closing popup
                this.saveCookie();
                if (callback) {
                    callback();
                }
            },
            async (e) => {
                const bootboxBody = e.target.querySelector('.bootbox-body');
                e.target.classList.add('loading');

                if (this.displayMode === 'requested') {
                    htmlContent = await this.loadPopupHtml();

                    if (!htmlContent) {
                        e.target.classList.remove('loading');
                        console.log('ContentPopup: No content loaded for requested popup with identifier: ' + this.popupIdentifier);
                        e.target.modal('hide');
                        return;
                    }
                }

                bootboxBody.innerHTML = htmlContent;
                e.target.classList.remove('loading');

                const htmlChangedEvent = new CustomEvent('plandent.html.changed', {detail: {target: e.target}});
                document.dispatchEvent(htmlChangedEvent);
                const reinitEvent = new CustomEvent('avency.reinit', {detail: {target: e.target}});
                document.dispatchEvent(reinitEvent);
            }
        );

        document.addEventListener('avency.mercuryai.chatbot.opened', () => {
            // @ts-ignore
            popupModal.modal('hide');
        })
    }

    async loadPopupHtml() {
        const response = await fetch('/?standalone=Avency.Plandent.ContentPopups:ContentPopup.Standalone&popup=' + this.popupIdentifier);
        let htmlContent = null;

        if (response.ok) {
            htmlContent = await response.text();
            if (this.isEmptyHTML(htmlContent)) {
                this.saveCookie();
                return;
            }
        } else {
            return;
        }
        return htmlContent;
    }

    saveCookie() {
        if (this.displayMode === 'requested') {
            // don't save cookie when shown per request
            return;
        }
        const viewedPopupCookie = getCookie(this.cookieName);
        const viewedPopups = viewedPopupCookie === '' ? {} : JSON.parse(viewedPopupCookie);
        viewedPopups[this.popupIdentifier] = new Date().getTime();
        if (viewedPopups['pending'] === this.popupIdentifier) {
            delete viewedPopups['pending'];
        }
        setCookie(this.cookieName, JSON.stringify(viewedPopups), null, this.cookieMode === 'persistent');
    }

    isEmptyHTML(htmlContent: string) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        if (!doc) {
            return true;
        }
        // check if one of the contained elements is not empty
        const items = doc.querySelector('.content-popup > .neos-contentcollection').children;

        if (items.length === 0) {
            return true;
        }

        for (const item of items) {
            if (this.isEmptyElement(item)) {
                return true;
            }
        }

        return false;
    }

    isEmptyElement(element: HTMLElement) {

        if (element.tagName === 'DIV' && element.childElementCount === 0) {
            // covers ProductTeaser, ProductTeaser Slider, ArticleBlock
            return true;
        }

        if (element.classList.contains('content-accessories')
            // check if ContentAccessories element is empty
            && !element.querySelector('.article-tab-navigation')
            && !element.querySelector('.product-teaser-list')) {
            return true;
        }

        if (element.classList.contains('filtered-article-list') && element.querySelector('.product-teaser-list').childElementCount === 0) {
            return true;
        }

        return false;
    }

    static initialize() {
        // check persistent cookie
        const viewedPopupCookiePersistent = getCookie('popup-view-log');
        const viewedPopupsPersistent = viewedPopupCookiePersistent === '' ? {} : JSON.parse(viewedPopupCookiePersistent);

        // check session cookie
        const viewedPopupCookieSession = getCookie('popup-view-log-session');
        const viewedPopupsSession = viewedPopupCookieSession === '' ? {} : JSON.parse(viewedPopupCookieSession);

        // merge data
        const viewedPopups = {...viewedPopupsPersistent, ...viewedPopupsSession};

        // filter popups already viewed
        const allocatedPopups = (window.contentPopupsAllocated || []).filter(popupData => !viewedPopups[popupData['identifier']]);
        const allocatedPopupsMap = Object.fromEntries(
            allocatedPopups.map(item => [item.identifier, item])
        );

        const sessionPendingIdentifier = viewedPopupsSession.pending || null;
        const persistentPendingIdentifier = viewedPopupsPersistent.pending || null;

        const automaticPopups = allocatedPopups.filter(popupData => {
            return (popupData['displayMode'] === 'delay' || popupData['displayMode'] === 'scroll-threshold')
                && popupData['identifier'] !== sessionPendingIdentifier
                && popupData['identifier'] !== persistentPendingIdentifier;
        });

        const eventPopups = allocatedPopups.filter(popupData => {
            return (popupData['displayMode'] === 'product-added-to-cart' || popupData['displayMode'] === 'product-requested')
                && popupData['identifier'] !== sessionPendingIdentifier
                && popupData['identifier'] !== persistentPendingIdentifier;
        });

        if (sessionPendingIdentifier && !viewedPopupsSession[sessionPendingIdentifier] && allocatedPopupsMap[sessionPendingIdentifier]) {

            // check if pending session popup
            const popupData = allocatedPopupsMap[sessionPendingIdentifier];
            popupData.displayMode = 'delay'
            popupData.displayDelay = 2;
            new ContentPopup(popupData);

        } else if (persistentPendingIdentifier && !viewedPopupsPersistent[persistentPendingIdentifier] && allocatedPopupsMap[persistentPendingIdentifier]) {

            // check if pending persistent popup
            const popupData = allocatedPopupsMap[persistentPendingIdentifier];
            popupData.displayMode = 'delay'
            popupData.displayDelay = 2;
            new ContentPopup(popupData);

        } else {
            if (automaticPopups.length > 0) {
                /** Only show one automatic popup, but there might be more conditional popups that need to be initialized */
                const popupToShow = automaticPopups[Math.floor(Math.random() * automaticPopups.length)];
                new ContentPopup(popupToShow);
            }
        }


        if (eventPopups.length > 0) {
            eventPopups.forEach(popupData => {
                new ContentPopup(popupData);
            });
        }

        // links
        const linkedPopups = document.querySelectorAll('a[href*=pld-popup]');
        linkedPopups.forEach((linkedPopup) => {
            const popupNodeInfo = linkedPopup.href.match(/pld-popup:\/\/(.*)#([a-z0-9\-]*)\??(.*)/);
            const nodePath = popupNodeInfo[1];
            const nodeIdentifier = popupNodeInfo[2];
            const additionalParams = popupNodeInfo[3] ? popupNodeInfo[3].split('&').map(pair => ({
                key: pair.split('=')[0],
                value: pair.split('=')[1]
            })) : [];
            linkedPopup.addEventListener('click', (e) => {
                new ContentPopup({
                    identifier: nodeIdentifier,
                    cookieMode: 'persistent',
                    displayMode: 'requested',
                    displayCartTargetProductItemIds: []
                });
                e.preventDefault();
            });
        });

    }
}

PageLoadedQueue.addFunction(ContentPopup.initialize);
